import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  loadWorkoutSessions,
  saveWorkoutSession,
  deleteWorkoutSession,
} from '../services/StorageService';
import {
  createWorkoutSessionObject,
  addLapToSession,
  addSegmentToSession,
  findSessionById,
} from '../utils/workoutSessionUtils';
import { Segment } from '../types/segment';
import { WorkoutSession, WorkoutLap } from '../types/workout';
import { Lap } from '../types/stopwatch';
import { convertLapToWorkoutLap } from '../utils/lapUtils';

// Define the LapData interface (moved from WorkoutContext)
export interface LapData {
  number: number;
  time: number;
  totalTime: number;
  timestamp?: string;
  isFinalLap?: boolean;
  pace?: number | null;
}

// Define the return type for this hook
export interface WorkoutSessionStateHook {
  // State
  workoutSessions: WorkoutSession[];
  currentSessionId: string | null;
  currentSession: WorkoutSession | null;
  lastCompletedSession: WorkoutSession | null;
  isReviewMode: boolean;

  // Setters - Remove if only used internally
  // setCurrentSessionId: (id: string | null) => void;
  // setIsReviewMode: (isReviewMode: boolean) => void;

  // Actions
  createWorkoutSession: () => string;
  saveLapData: (lapData: LapData) => LapData | null;
  saveSegmentData: (segmentData: Segment) => Segment | null;
  completeWorkoutSession: (finalLap: Lap | null, paceUnit?: 'mile' | 'km') => boolean | null;
  deleteSession: (sessionId: string) => boolean;

  // Review mode actions
  enterReviewMode: () => void;
  exitReviewMode: () => void;
  selectSessionForReview: (sessionId: string) => void;
}

/**
 * Convert LapData to Lap (for internal use)
 */
const convertLapDataToLap = (lapData: LapData): Lap => {
  return {
    id: `lap-${lapData.number}-${Date.now()}`,
    number: lapData.number,
    time: lapData.time,
    cumulativeTime: lapData.totalTime,
    timestamp: lapData.timestamp ? new Date(lapData.timestamp).getTime() : Date.now(),
    isActive: false,
  };
};

/**
 * Custom hook for managing workout session state
 */
const useWorkoutSessionState = (): WorkoutSessionStateHook => {
  // State for workout sessions
  const [workoutSessions, setWorkoutSessions] = useState<WorkoutSession[]>([]);
  // Current workout session ID
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  // State for the last completed session
  const [lastCompletedSession, setLastCompletedSession] = useState<WorkoutSession | null>(null);
  // Whether we're in review mode
  const [isReviewMode, setIsReviewMode] = useState<boolean>(false);
  // Store recorded segment IDs to prevent duplicates
  const [recordedSegmentIds, setRecordedSegmentIds] = useState<Set<string>>(new Set());

  // On component mount, load saved data from localStorage
  useEffect(() => {
    try {
      const sessions = loadWorkoutSessions();
      if (sessions.length > 0) {
        setWorkoutSessions(sessions as WorkoutSession[]);
      }
    } catch (error) {
      console.error('Error loading saved workout sessions:', error);
    }
  }, []);

  // Calculate currentSession based on state
  const currentSession = useMemo<WorkoutSession | null>(() => {
    // Ensure workoutSessions is treated as an array
    const sessions = Array.isArray(workoutSessions) ? workoutSessions : [];
    return findSessionById(sessions, currentSessionId) || null;
  }, [workoutSessions, currentSessionId]);

  // Create a new workout session
  const createWorkoutSession = useCallback((): string => {
    // Create a new session object
    const newSession = createWorkoutSessionObject();

    // Update state
    setCurrentSessionId(newSession.id);
    setWorkoutSessions(prevSessions => [...prevSessions, newSession as WorkoutSession]);
    setRecordedSegmentIds(new Set());
    setIsReviewMode(false);
    setLastCompletedSession(null);

    // Save to storage
    saveWorkoutSession(newSession);

    return newSession.id;
  }, []);

  // Save lap data to the current session
  const saveLapData = useCallback(
    (lapData: LapData): LapData | null => {
      if (!currentSessionId) return null;

      // Find the session using the ID, not the potentially stale memoized currentSession
      const sessionToUpdate = findSessionById(
        workoutSessions as WorkoutSession[],
        currentSessionId
      );
      const updatedSession = addLapToSession(sessionToUpdate, convertLapDataToLap(lapData));

      if (updatedSession) {
        setWorkoutSessions(prevSessions =>
          prevSessions.map(session => (session.id === currentSessionId ? updatedSession : session))
        );
      }

      return lapData;
    },
    [currentSessionId, workoutSessions] // Dependency remains on the ID and the list
  );

  // Save segment data to the current session
  const saveSegmentData = useCallback(
    (segmentData: Segment): Segment | null => {
      if (!currentSessionId || !segmentData) return null;

      if (recordedSegmentIds.has(segmentData.id)) {
        return null;
      }

      // Find the session using the ID
      const sessionToUpdate = findSessionById(
        workoutSessions as WorkoutSession[],
        currentSessionId
      );
      const updatedSession = addSegmentToSession(sessionToUpdate, segmentData);

      if (updatedSession) {
        setWorkoutSessions(prevSessions =>
          prevSessions.map(session => (session.id === currentSessionId ? updatedSession : session))
        );

        setRecordedSegmentIds(prevIds => {
          const newIds = new Set(prevIds);
          newIds.add(segmentData.id);
          return newIds;
        });
      }

      return segmentData;
    },
    [currentSessionId, recordedSegmentIds, workoutSessions] // Dependency remains on the ID and the list
  );

  // Complete the current workout session
  const completeWorkoutSession = useCallback(
    (finalLap: Lap | null, paceUnit: 'mile' | 'km' = 'mile'): boolean | null => {
      if (!currentSessionId) return null;

      // Find the session to complete
      const sessionToComplete = findSessionById(
        workoutSessions as WorkoutSession[],
        currentSessionId
      );

      if (!sessionToComplete) {
        console.error('Session not found for completion:', currentSessionId);
        return false;
      }

      // Create a mutable copy of the session
      const updatedSession: WorkoutSession = { ...sessionToComplete };

      // Add the final lap if it exists
      if (finalLap) {
        const workoutLap: WorkoutLap = convertLapToWorkoutLap(finalLap);
        updatedSession.laps = [...updatedSession.laps, workoutLap];
        updatedSession.lapCount = (updatedSession.lapCount || 0) + 1;
        // Update totalTime based on the final lap's cumulative time
        updatedSession.totalTime = workoutLap.cumulativeTime; // Use cumulativeTime from WorkoutLap
      } else if (updatedSession.laps.length > 0) {
        // If no final lap but previous laps exist, use the last lap's cumulative time
        const lastLap = updatedSession.laps[updatedSession.laps.length - 1];
        updatedSession.totalTime = lastLap?.cumulativeTime ?? 0; // Use nullish coalescing
      } else {
        // If no laps at all, totalTime remains 0
        updatedSession.totalTime = 0;
      }

      // Make sure all segments are included in the completed session
      // This is necessary for lap pace calculations to work in the history view
      if (updatedSession.segments) {
        // Ensure we have a deep copy of the segments
        updatedSession.segments = JSON.parse(JSON.stringify(updatedSession.segments));

        // Make sure each lap has its pace calculated and stored
        updatedSession.laps.forEach((lap, index) => {
          const lapNumber = index + 1;
          const lapSegments = updatedSession.segments[lapNumber] || [];

          // Only calculate pace if we have segments with distance and the lap doesn't already have a pace
          // Also recalculate if pace is 0 which can happen due to conversion issues
          if (lapSegments.length > 0 && (!lap.pace || !isFinite(lap.pace) || lap.pace === 0)) {
            const totalLapDistance = lapSegments.reduce(
              (sum, segment) => sum + segment.distance,
              0
            );
            if (totalLapDistance > 0 && lap.time > 0) {
              // Store the pace in the lap object for direct access in history view
              // Always store pace in minutes/mile regardless of current display unit
              // This ensures consistent storage and allows proper conversion when displaying
              lap.pace = calculatePaceHelper(lap.time, totalLapDistance, paceUnit);
            }
          }
        });
      }

      // Mark the session as complete
      updatedSession.completed = true;
      updatedSession.completedAt = new Date();

      // Record the pace unit used at completion time for proper conversion later
      updatedSession.paceUnit = paceUnit;

      // Perform a single save with the fully updated session
      const success = saveWorkoutSession(updatedSession);

      if (success) {
        // Update state ONLY after successful save
        setWorkoutSessions(prevSessions =>
          prevSessions.map(session => (session.id === currentSessionId ? updatedSession : session))
        );

        // Clear current session ID
        setCurrentSessionId(null);

        // Update lastCompletedSession
        setLastCompletedSession(updatedSession);

        return true;
      } else {
        console.error('Failed to save completed session:', currentSessionId);
        return false;
      }
    },
    [currentSessionId, workoutSessions, setLastCompletedSession]
  );

  // Helper function to calculate pace based on unit
  const calculatePaceHelper = (
    timeMs: number,
    distanceMeters: number,
    paceUnit: 'mile' | 'km' = 'mile'
  ): number => {
    // Convert time to minutes
    const timeMinutes = timeMs / 60000;

    // Convert distance based on pace unit
    const conversionFactor = paceUnit === 'mile' ? 1609.34 : 1000; // meters per mile or km
    const distance = distanceMeters / conversionFactor;

    // Calculate pace in minutes per unit (mile or km)
    return timeMinutes / distance;
  };

  // Delete a workout session
  const deleteSession = useCallback(
    (sessionId: string): boolean => {
      if (!sessionId) return false;

      // Delete from storage
      const success = deleteWorkoutSession(sessionId);

      if (success) {
        // Update state
        setWorkoutSessions(prevSessions =>
          prevSessions.filter(session => session.id !== sessionId)
        );

        // If we deleted the current session, clear it
        if (sessionId === currentSessionId) {
          setCurrentSessionId(null);
        }
      }

      return success;
    },
    [currentSessionId]
  );

  // Enter review mode
  const enterReviewMode = useCallback(() => {
    setIsReviewMode(true);
  }, []);

  // Exit review mode
  const exitReviewMode = useCallback(() => {
    setIsReviewMode(false);
    setCurrentSessionId(null); // Clear selected session when exiting review mode
  }, []);

  // Select a session for review
  const selectSessionForReview = useCallback(
    (sessionId: string) => {
      if (!sessionId) return;
      const session = findSessionById(workoutSessions as WorkoutSession[], sessionId);
      if (session) {
        setCurrentSessionId(sessionId);
        setIsReviewMode(true);
      }
    },
    [workoutSessions]
  );

  // Return state and actions
  return {
    workoutSessions,
    currentSessionId,
    currentSession,
    lastCompletedSession,
    isReviewMode,
    // Setters removed
    createWorkoutSession,
    saveLapData,
    saveSegmentData,
    completeWorkoutSession,
    deleteSession,
    enterReviewMode,
    exitReviewMode,
    selectSessionForReview,
  };
};

export default useWorkoutSessionState;
